// Per-page title, description, canonical, Open Graph, and Twitter tags.
// Empty CMS strings must not wipe the homepage defaults in public/index.html.
import { useEffect } from "react";

const TITLE_SUFFIX = " | vLookUp";

export const DEFAULT_TITLE =
  "vLookUp | Creating impactful mentoring connections for women";
export const DEFAULT_DESCRIPTION =
  "vLookUp offers one-on-one, domain-specific conversations and career mentoring  between aspiring women leaders and experienced industry leaders.";
export const DEFAULT_OG_IMAGE =
  "https://www.vlookup.ai/assets/images/vlookup-og-image.png";

export function meaningfulMeta(value) {
  return typeof value === "string" && value.replace(/\s+/g, " ").trim().length > 12;
}

export function stripHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

export function excerptText(value, max = 160) {
  const text = stripHtml(value);
  if (!text) return "";
  if (text.length <= max) return text;
  return text.slice(0, max - 1).replace(/\s+\S*$/, "") + "…";
}

export function absoluteUrl(url) {
  if (!url) return "";
  const raw = String(url).trim();
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("//")) {
    return (typeof window !== "undefined" ? window.location.protocol : "https:") + raw;
  }
  if (typeof window === "undefined") return raw;
  if (raw.startsWith("/")) return window.location.origin + raw;
  return raw;
}

function upsertMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function usePageMeta({ title, description, image, type } = {}) {
  useEffect(() => {
    const resolvedTitle = meaningfulMeta(title)
      ? /vlookup/i.test(title)
        ? title.trim()
        : title.trim() + TITLE_SUFFIX
      : DEFAULT_TITLE;
    const resolvedDesc = meaningfulMeta(description)
      ? description.replace(/\s+/g, " ").trim()
      : DEFAULT_DESCRIPTION;
    const resolvedImage = absoluteUrl(image) || DEFAULT_OG_IMAGE;
    const resolvedType = type || "website";
    const url = window.location.origin + window.location.pathname;

    document.title = resolvedTitle;
    upsertMeta("name", "description", resolvedDesc);
    upsertLink("canonical", url);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", resolvedType);
    upsertMeta("property", "og:title", resolvedTitle);
    upsertMeta("property", "og:description", resolvedDesc);
    upsertMeta("property", "og:image", resolvedImage);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", resolvedTitle);
    upsertMeta("name", "twitter:description", resolvedDesc);
    upsertMeta("name", "twitter:image", resolvedImage);
  }, [title, description, image, type]);
}

export function useJsonLd(id, data) {
  const serialized = data ? JSON.stringify(data) : "";
  useEffect(() => {
    if (!id) return undefined;
    if (!serialized) {
      const stale = document.getElementById(id);
      if (stale && stale.parentNode) stale.parentNode.removeChild(stale);
      return undefined;
    }
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.text = serialized;
    return () => {
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
  }, [id, serialized]);
}
